import { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Loader2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { OtpInput } from "./OtpInput";

interface MfaVerifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerify: (code: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function MfaVerifyDialog({
  open,
  onOpenChange,
  onVerify,
  isLoading = false,
  error = null,
}: MfaVerifyDialogProps) {
  const [code, setCode] = useState("");

  const handleVerify = async () => {
    if (code.length !== 6) return;
    await onVerify(code);
  };

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center"
          >
            <Shield className="w-8 h-8 text-primary" />
          </motion.div>
          <DialogTitle className="text-center">
            Two-Factor Authentication
          </DialogTitle>
          <DialogDescription className="text-center">
            Enter the 6-digit code from your authenticator app
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <OtpInput
            value={code}
            onChange={handleCodeChange}
            disabled={isLoading}
            error={!!error}
            autoFocus
          />

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          <Button
            onClick={handleVerify}
            disabled={code.length !== 6 || isLoading}
            className="w-full h-12"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Open your authenticator app (Google Authenticator, Authy, etc.) and
            enter the code shown for Echopeak Labs
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

